import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';
import { candidateActionsApi } from '../../services/candidateActionsApi';
import './CandidateToolbarModals.scss';

interface ForwardCvModalProps {
  cvId: number;
  candidateName: string;
  onClose: () => void;
  onSent?: (recipient: string) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Siųsti el. paštu" — forward a short CV summary to a colleague's email
 * address. The receiving colleague gets a deep link back into the CV
 * detail page; viewing the full record still requires a same-company
 * employer login.
 */
const ForwardCvModal: React.FC<ForwardCvModalProps> = ({
  cvId,
  candidateName,
  onClose,
  onSent,
}) => {
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    const trimmedRecipient = recipient.trim();
    const trimmedMessage = message.trim();

    if (!EMAIL_RE.test(trimmedRecipient)) {
      setError('Įveskite teisingą gavėjo el. paštą');
      return;
    }
    if (trimmedMessage.length > 2000) {
      setError('Žinutė per ilga (iki 2000 simbolių)');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await candidateActionsApi.forwardByEmail(
        cvId,
        trimmedRecipient,
        trimmedMessage || undefined,
      );
      onSent?.(trimmedRecipient);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Nepavyko išsiųsti laiško');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="candidate-toolbar-modal-overlay" onClick={submitting ? undefined : onClose}>
      <div
        className="candidate-toolbar-modal candidate-toolbar-modal--forward"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="candidate-toolbar-modal__close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Uždaryti"
        >
          <X size={20} />
        </button>

        <div className="candidate-toolbar-modal__header">
          <Mail size={20} />
          <h2>Siųsti CV el. paštu</h2>
        </div>

        <p className="candidate-toolbar-modal__subtitle">
          {candidateName} — kolega gaus trumpą CV santrauką ir nuorodą peržiūrai.
        </p>

        <div className="candidate-toolbar-modal__field">
          <label>Gavėjo el. paštas</label>
          <input
            type="email"
            placeholder="kolega@imone.lt"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        </div>

        <div className="candidate-toolbar-modal__field">
          <label>Žinutė (neprivaloma)</label>
          <textarea
            rows={4}
            maxLength={2000}
            placeholder="Pasidalink trumpa pastaba, pvz. „pažiūrėk šį kandidatą Vilniaus pozicijai“..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={submitting}
          />
        </div>

        {error && <div className="candidate-toolbar-modal__error">{error}</div>}

        <div className="candidate-toolbar-modal__actions">
          <button
            type="button"
            className="candidate-toolbar-modal__btn candidate-toolbar-modal__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Atšaukti
          </button>
          <button
            type="button"
            className="candidate-toolbar-modal__btn candidate-toolbar-modal__btn--primary"
            onClick={handleSend}
            disabled={submitting}
          >
            {submitting ? 'Siunčiama...' : 'Išsiųsti'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForwardCvModal;
